/**
 * DataSecurityServiceCard Component
 *
 * Displays a service card for Data Security features (BAA, COI, Data Retention).
 * Shows status-based rendering with Setup button, Processing badge, or Active state.
 */

import React from 'react';
import { useThemeContext } from '@context/theme/context';
import { CheckCircle, HourglassEmpty } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export type ServiceStatus = 'not_configured' | 'processing' | 'active';

interface DataSecurityServiceCardProps {
  title: string;
  description: string;
  status: ServiceStatus;
  onSetup: () => void;
  setupButtonText?: string;
}

export const DataSecurityServiceCard: React.FC<DataSecurityServiceCardProps> = ({
  title,
  description,
  status,
  onSetup,
  setupButtonText = 'Setup',
}) => {
  const { brandLanguage } = useThemeContext();

  const renderStatusContent = () => {
    switch (status) {
      case 'active':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip
              icon={<CheckCircle sx={{ fontSize: 18 }} />}
              label="Active"
              size="medium"
              sx={{
                fontWeight: 600,
                px: 1,
                backgroundColor: theme => alpha(theme.palette.success.main, 0.1),
                color: theme => theme.palette.success.dark,
                border: '1px solid',
                borderColor: theme => theme.palette.success.dark,
                '& .MuiChip-icon': {
                  color: theme => theme.palette.success.dark,
                },
              }}
            />
            <Link
              href={`https://${brandLanguage.brandSite}/contact`}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ color: 'text.secondary', textDecoration: 'underline' }}
            >
              Contact to edit
            </Link>
          </Box>
        );
      case 'processing':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip
              icon={<HourglassEmpty sx={{ fontSize: 18 }} />}
              label="Processing"
              size="medium"
              sx={{
                fontWeight: 600,
                px: 1,
                backgroundColor: theme => alpha(theme.palette.warning.main, 0.1),
                color: theme => theme.palette.warning.dark,
                border: '1px solid',
                borderColor: theme => theme.palette.warning.dark,
                '& .MuiChip-icon': {
                  color: theme => theme.palette.warning.dark,
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Please allow 2-3 business days
            </Typography>
          </Box>
        );
      case 'not_configured':
      default:
        return (
          <Button
            variant="outlined"
            color="primary"
            onClick={onSetup}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 100,
            }}
          >
            {setupButtonText}
          </Button>
        );
    }
  };

  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: 'grey.200',
      }}
    >
      <CardContent sx={{ pb: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0 }}>{renderStatusContent()}</Box>
        </Box>
      </CardContent>
    </Card>
  );
};
